import { customEval, iaRa } from 'lib/helpers/utility';

const setNestedValue = (row, field, value) => {
  const keys = field.split('.');
  const lastKey = keys.pop();

  const target = keys.reduce((acc, key) => {
    acc[key] = acc[key] && typeof acc[key] === 'object' ? { ...acc[key] } : {};
    return acc[key];
  }, row);

  target[lastKey] = value;
};

const resolveValue = (column, row, { skipFormatter = false } = {}) => {
  const { field, valueGetter, valueFormatter } = column;
  let value = customEval(row, field);

  try {
    if (valueGetter) {
      value = valueGetter({ value, data: row, colDef: column });
    }

    if (!skipFormatter && valueFormatter) {
      return valueFormatter({ value, data: row, colDef: column }) ?? value;
    }
  } catch (error) {
    return customEval(row, field);
  }

  return value;
};

export const applyColumnFormatters = ({ rows, columns, excludedFields = [] }) => {
  const safeColumns = iaRa(columns);
  const excluded = new Set(iaRa(excludedFields));

  const formattedColumns = safeColumns.filter(
    (col) => col?.field && !excluded.has(col.field) && (col.valueFormatter || col.valueGetter)
  );

  // Totalled columns keep raw numbers so the PDF can sum them, but a column that only
  // exists through a valueGetter (e.g. total_charge) still has to be materialised, and a
  // missing amount totals as zero rather than rendering as a blank cell.
  const totalsColumns = safeColumns.filter((col) => col?.field && excluded.has(col.field));

  if (!formattedColumns.length && !totalsColumns.length) return { rows, columns: safeColumns };

  const formattedFields = new Set(formattedColumns.map((col) => col.field));

  return {
    rows: iaRa(rows).map((row) => {
      const formattedRow = { ...row };

      formattedColumns.forEach((column) =>
        setNestedValue(formattedRow, column.field, resolveValue(column, row))
      );

      totalsColumns.forEach((column) => {
        const value = resolveValue(column, row, { skipFormatter: true });
        setNestedValue(formattedRow, column.field, value ?? 0);
      });

      return formattedRow;
    }),
    columns: safeColumns.map((col) =>
      formattedFields.has(col.field) ? { ...col, preformatted: true } : col
    )
  };
};
